/**
 * Everything that floats over the map.
 *
 * One DOM for both layouts. On a wide screen the category cards are a stack at
 * the top left and the area panel is a card at the top right; at 760 px and
 * under, the same cards become a sheet that rises from a bottom bar and the
 * same panel becomes a sheet docked above it. That is a stylesheet decision,
 * not a second interface -- see `style.css` -- so there is one set of controls
 * with one set of behaviours and no chance of the two drifting.
 *
 * Nothing here is written per category. The cards, their colours, their
 * legends, the schools phase control and the transport destination are all
 * generated from the manifest, so
 * a fifth category appears in the interface by appearing in the emitter.
 */

import { blockedHtml, placeholderHtml } from "./ads.ts";
import { escapeHtml } from "./format.ts";
import type { LayerSpec, Manifest } from "./manifest.ts";
import { PRESETS, matching } from "./presets.ts";
import { rampStops } from "./ramp.ts";
import type { State } from "./state.ts";

export interface ChromeCallbacks {
  onWeight(name: string, value: number): void;
  /** a card was tapped: select that category, or return to the blend */
  onSelect(name: string | null): void;
  onPhase(phase: string): void;
  onCloseArea(): void;
  onToggleDetail(): void;
  /** a detail line's `i` was tapped: show where its figures came from, or hide it */
  onInfo(name: string): void;
  /** a scenario chip was tapped: move every weight to that preset */
  onPreset(name: string): void;
  onCloseAd(): void;
  /** the transport card's "Travel to" was tapped, or the picker was put away */
  onPicker(open: boolean): void;
  /** the picker's default was chosen: back to what the layer measures by default */
  onResetDestination(): void;
}

/**
 * The one line of instruction left, and it is only on a narrow screen, where
 * the cards live behind a bar and a reader has no way of knowing what the bar
 * opens. On a wide screen the cards are simply there, and a sentence over them
 * explaining that a card can be tapped is a sentence about a card.
 */
const HINT_NARROW = "Tap a category to view its layer · slide to set its importance";

/** How far a finger must travel before a touch becomes a drag and not a tap. */
const GRAB_PX = 8;

/** How far down the sheet must come to close rather than spring back. */
const DISMISS_PX = 64;

/** A flick: fast enough downward, in px per ms, that the distance stops mattering. */
const DISMISS_VELOCITY = 0.5;

/** How long a finished drag goes on eating clicks, if one never arrives. */
const SWALLOW_MS = 350;

function card(layer: LayerSpec, state: State, destination: string): string {
  const weight = Math.round(state.weights[layer.name] ?? 0);
  const active = state.view === layer.name;
  const dim = state.view !== null && !active;
  const phases = layer.phases;
  const segmented = phases
    ? `<div class="segmented" role="group">${phases.order.map((key) => `
        <button class="seg${state.phase === key ? " is-on" : ""}" data-phase="${key}">
          ${escapeHtml(phases.labels[key] ?? key)}
        </button>`).join("")}</div>`
    : "";
  const travel = layer.destinations
    ? `<button class="dest" data-action="open-picker">
        <span class="dest-to">Travel to</span>
        <span class="dest-name" data-dest-name>${escapeHtml(destination)}</span>
        <span class="dest-go" aria-hidden="true">&rsaquo;</span>
      </button>`
    : "";

  return `
    <div class="card${active ? " is-active" : ""}${dim ? " is-dim" : ""}"
         data-card="${layer.name}" style="--accent:${layer.colour}">
      <div class="card-top">
        <span class="chip" style="background:${layer.colour}"></span>
        <span class="card-name">${escapeHtml(layer.title)}</span>
        <span class="card-weight">${weight}%</span>
      </div>
      <input class="slider" type="range" min="0" max="100" step="1" value="${weight}"
             data-slider="${layer.name}" aria-label="${escapeHtml(layer.title)} importance" />
      ${segmented}
      ${travel}
    </div>`;
}

export class Chrome {
  readonly mapContainer: HTMLElement;
  private readonly root: HTMLElement;
  private readonly manifest: Manifest;
  private readonly callbacks: ChromeCallbacks;
  private readonly cards: HTMLElement;
  private readonly presets: HTMLElement;
  private readonly sheet: HTMLElement;
  private readonly grabber: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly ad: HTMLElement;
  private readonly legend: HTMLElement;
  private readonly modeWide: HTMLElement;
  private readonly modeNarrow: HTMLElement;
  private readonly status: HTMLElement;
  private readonly picker: HTMLElement;
  /** what the transport card says it travels to */
  private destination: string;
  /** True for the instant after a drag, so its click lands on nothing. */
  private dragged = false;
  private dragTimer = 0;

  constructor(root: HTMLElement, manifest: Manifest, callbacks: ChromeCallbacks) {
    this.root = root;
    this.manifest = manifest;
    this.callbacks = callbacks;
    const travelLayer = manifest.layers.find((layer) => layer.destinations);
    const home = travelLayer?.destinations?.default_label ?? "";
    this.destination = home;
    root.classList.add("app");
    root.innerHTML = `
      <header class="top">
        <span class="brand"><span class="brand-mark"></span>PostcodeHunt</span>
        <span class="mode" data-mode-wide></span>
      </header>
      <div class="map" id="prod-map"></div>
      <div class="sheet" data-sheet>
        <button class="grabber" data-action="close-sheet" aria-label="Close categories"></button>
        <p class="hint">${escapeHtml(HINT_NARROW)}</p>
        <div class="presets" data-presets role="group" aria-label="Scenarios">
          ${PRESETS.map((preset) => `
            <button class="preset${preset.reset ? " is-reset" : ""}" data-preset="${preset.name}"
                    title="${escapeHtml(preset.means)}">
              ${escapeHtml(preset.title)}
            </button>`).join("")}
        </div>
        <div class="scale" aria-hidden="true">
          <span>Doesn't matter</span><span class="scale-rule"></span><span>Matters a lot</span>
        </div>
        <div class="cards" data-cards></div>
        <div class="picker" data-picker hidden>
          <div class="picker-top">
            <button class="picker-back" data-action="close-picker" aria-label="Back">&lsaquo;</button>
            <span class="picker-title">Select destination</span>
          </div>
          <p class="picker-hint">Tap a highlighted station on the map</p>
          <button class="picker-home is-on" data-action="reset-destination">${escapeHtml(home)}</button>
        </div>
      </div>
      <div class="legend" data-legend></div>
      <aside class="panel" data-panel hidden></aside>
      <aside class="strip" data-strip hidden aria-label="Advertisement">
        <span class="strip-tag">Ad</span>
        <div class="strip-body" data-strip-body></div>
        <button class="strip-close" data-action="close-ad" aria-label="Close ad">&times;</button>
      </aside>
      <footer class="bottom">
        <span class="mode-narrow" data-mode-narrow></span>
        <button class="pill" data-action="toggle-sheet">Categories</button>
      </footer>
      <p class="status" data-status></p>`;

    this.mapContainer = root.querySelector("#prod-map")!;
    this.cards = root.querySelector("[data-cards]")!;
    this.presets = root.querySelector("[data-presets]")!;
    this.sheet = root.querySelector("[data-sheet]")!;
    this.grabber = root.querySelector("[data-action='close-sheet']")!;
    this.panel = root.querySelector("[data-panel]")!;
    this.ad = root.querySelector("[data-strip]")!;
    this.legend = root.querySelector("[data-legend]")!;
    this.modeWide = root.querySelector("[data-mode-wide]")!;
    this.modeNarrow = root.querySelector("[data-mode-narrow]")!;
    this.status = root.querySelector("[data-status]")!;
    this.picker = root.querySelector("[data-picker]")!;

    this.bind();
    this.dragToClose(this.sheet, "sheet-dragging",
                     () => this.root.classList.contains("sheet-open"),
                     () => this.closeSheet());
    this.dragToClose(this.panel, "panel-dragging",
                     () => !this.panel.hidden,
                     () => this.callbacks.onCloseArea());
  }

  /**
   * Whether the cards are a sheet at all.
   *
   * The grabber is the element the stylesheet shows in the narrow layout and
   * nowhere else, so asking whether it is displayed asks the stylesheet where
   * the breakpoint is, rather than writing 760 px down a second time in here
   * for the two to drift apart later.
   */
  private isSheet(): boolean {
    return window.getComputedStyle(this.grabber).display !== "none";
  }

  /**
   * Pull a sheet down to put it away.
   *
   * Both of the things that dock at the bottom of a narrow screen are dragged
   * shut the same way, because to a reader they are the same object: the
   * categories, and the panel a tap on the map opens. Past `DISMISS_PX`, or on
   * a flick faster than `DISMISS_VELOCITY`, it goes; anything shorter springs
   * back, which is what makes the gesture safe to try.
   *
   * Touch events rather than pointer events, and `touchmove` bound non-passive
   * so that it can call `preventDefault`. A sheet that scrolls is a sheet the
   * browser believes it owns the vertical gesture on: left to itself it starts
   * a scroll and answers with `pointercancel`, which kills the drag on the
   * first millimetre. Refusing the default on the move that begins the drag is
   * what takes the gesture back, and there is no pointer-events equivalent
   * short of making the sheet unscrollable.
   *
   * Two things a downward drag must not steal. A sheet scrolled away from its
   * top is a list being read, so the drag only begins at `scrollTop` 0 and a
   * reader pulling a long list back up scrolls it. And a finger that started on
   * a slider is setting a weight however much it wanders, so it is left alone.
   */
  private dragToClose(sheet: HTMLElement, flag: string,
                      isOpen: () => boolean, close: () => void): void {
    let startY = 0;
    let dragging = false;
    let tracking = false;
    let velocity = 0;
    let sample = { y: 0, at: 0 };

    const finish = (commit: boolean): void => {
      if (dragging) {
        // Ordered so the sheet animates on from where the finger left it: the
        // transition comes back first, the state changes, and only then is the
        // inline offset dropped so the stylesheet's own target takes over.
        // Clearing it any earlier snaps the sheet to one end and slides from there.
        this.root.classList.remove(flag);
        if (commit) close();
        sheet.style.transform = "";
        this.swallowNextClick();
      }
      tracking = false;
      dragging = false;
    };

    sheet.addEventListener("touchstart", (event) => {
      if (event.touches.length !== 1) return finish(false);
      const touch = event.touches[0]!;
      if (!this.isSheet() || !isOpen()) return;
      if ((event.target as HTMLElement).closest("[data-slider]")) return;
      if (sheet.scrollTop > 0) return;
      tracking = true;
      startY = touch.clientY;
      velocity = 0;
      sample = { y: touch.clientY, at: event.timeStamp };
    }, { passive: true });

    sheet.addEventListener("touchmove", (event) => {
      if (!tracking) return;
      const touch = event.touches[0];
      if (!touch) return;
      const dy = touch.clientY - startY;
      if (!dragging) {
        // an upward finger is reading the list, and it is not this gesture
        if (dy < 0) return finish(false);
        if (dy < GRAB_PX) return;
        dragging = true;
        this.root.classList.add(flag);
      }
      event.preventDefault();
      const dt = Math.max(event.timeStamp - sample.at, 1);
      velocity = (touch.clientY - sample.y) / dt;
      sample = { y: touch.clientY, at: event.timeStamp };
      sheet.style.transform = `translateY(${Math.max(dy, 0)}px)`;
    }, { passive: false });

    sheet.addEventListener("touchend", (event) => {
      if (!tracking) return;
      const dy = (event.changedTouches[0]?.clientY ?? startY) - startY;
      finish(dy > DISMISS_PX || velocity > DISMISS_VELOCITY);
    });

    sheet.addEventListener("touchcancel", () => finish(false));
  }

  /** Put the narrow screen's sheet away, and the picker with it if it was open. */
  private closeSheet(): void {
    this.root.classList.remove("sheet-open");
    if (!this.picker.hidden) this.callbacks.onPicker(false);
  }

  /**
   * Eat the click a finished drag leaves behind.
   *
   * A touch that moved still ends with a click on whatever it happened to lift
   * over, and on these two sheets that is a category row, a source link or the
   * grabber itself -- so a drag the reader chose to abandon would act as a tap
   * on the thing under their thumb. Consumed in the capture phase, before any
   * of the handlers below see it, and released on a timer in case no click
   * follows the drag at all.
   */
  private swallowNextClick(): void {
    this.dragged = true;
    window.clearTimeout(this.dragTimer);
    this.dragTimer = window.setTimeout(() => { this.dragged = false; }, SWALLOW_MS);
  }

  private bind(): void {
    this.root.addEventListener("click", (event) => {
      if (!this.dragged) return;
      this.dragged = false;
      event.stopPropagation();
      event.preventDefault();
    }, { capture: true });

    // Delegated, because the cards are re-rendered whenever the state moves and
    // listeners bound to a card would die with it.
    this.cards.addEventListener("input", (event) => {
      const input = (event.target as HTMLElement).closest("[data-slider]");
      if (!(input instanceof HTMLInputElement)) return;
      this.callbacks.onWeight(input.dataset["slider"]!, Number(input.value));
    });

    this.cards.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      const phase = target.closest("[data-phase]");
      if (phase instanceof HTMLElement) {
        // the phase control lives inside a card, and choosing a phase is not
        // choosing the card
        event.stopPropagation();
        this.callbacks.onPhase(phase.dataset["phase"]!);
        return;
      }
      // dragging a slider must never change which category is selected, and
      // neither does a button inside the card: the root handles those
      if (target.closest("[data-slider]") || target.closest("[data-action]")) return;
      const card = target.closest("[data-card]");
      if (card instanceof HTMLElement) this.callbacks.onSelect(card.dataset["card"]!);
    });

    this.root.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      // A panel row is the same control as the card on the left, and the `i`
      // inside its detail line is not: the `i` is looked for first, so opening
      // a source note does not also change which layer is on the map.
      const info = target.closest("[data-info]");
      if (info instanceof HTMLElement) {
        this.callbacks.onInfo(info.dataset["info"]!);
        return;
      }
      const preset = target.closest("[data-preset]");
      if (preset instanceof HTMLElement) {
        this.callbacks.onPreset(preset.dataset["preset"]!);
        return;
      }
      const select = target.closest("[data-select]");
      if (select instanceof HTMLElement) {
        this.callbacks.onSelect(select.dataset["select"]!);
        return;
      }
      const action = target.closest("[data-action]");
      if (!(action instanceof HTMLElement)) return;
      switch (action.dataset["action"]) {
        case "toggle-sheet":
          if (this.root.classList.contains("sheet-open")) this.closeSheet();
          else this.root.classList.add("sheet-open");
          break;
        case "close-sheet":
          this.closeSheet();
          break;
        case "close-area":
          this.callbacks.onCloseArea();
          break;
        case "toggle-detail":
          this.callbacks.onToggleDetail();
          break;
        case "close-ad":
          this.callbacks.onCloseAd();
          break;
        case "open-picker":
          this.callbacks.onPicker(true);
          break;
        case "close-picker":
          this.callbacks.onPicker(false);
          break;
        case "reset-destination":
          this.callbacks.onResetDestination();
          break;
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !this.picker.hidden) this.callbacks.onPicker(false);
    });
  }

  /** Redraw the controls from the state. Cheap enough to do on every change. */
  render(state: State): void {
    const active = state.view
      ? this.manifest.layers.find((layer) => layer.name === state.view) ?? null
      : null;

    this.renderCards(state);

    // Marked by what the weights are, not by what was last clicked: drag any
    // slider and the chip stops claiming to describe the map.
    const on = matching(state.weights, this.manifest);
    for (const chip of this.presets.querySelectorAll<HTMLElement>("[data-preset]")) {
      chip.classList.toggle("is-on", chip.dataset["preset"] === on?.name);
    }

    const label = active ? `${active.title} layer` : "Blended suitability";
    this.modeWide.textContent = label;
    this.modeNarrow.textContent = active ? `${active.title} layer` : "Blended view";
    this.root.classList.toggle("has-view", state.view !== null);

    const colour = active?.colour ?? this.manifest.blend.colour;
    const [low, high] = active?.legend ?? this.manifest.blend.legend;
    this.legend.innerHTML =
      `<span class="legend-label">${escapeHtml(low)}</span>` +
      `<span class="legend-bar" style="background:linear-gradient(to right,` +
      `${rampStops(colour).join(",")})"></span>` +
      `<span class="legend-label">${escapeHtml(high)}</span>`;
  }

  /**
   * The cards are built once and updated in place after that. Replacing them
   * on every change would destroy a range input while it is being dragged,
   * which ends the drag after the first step.
   */
  private renderCards(state: State): void {
    if (!this.cards.childElementCount) {
      this.cards.innerHTML = this.manifest.layers
        .map((layer) => card(layer, state, this.destination)).join("");
      return;
    }
    for (const layer of this.manifest.layers) {
      const el = this.cards.querySelector<HTMLElement>(`[data-card="${layer.name}"]`);
      if (!el) continue;
      const weight = Math.round(state.weights[layer.name] ?? 0);
      const active = state.view === layer.name;
      el.classList.toggle("is-active", active);
      el.classList.toggle("is-dim", state.view !== null && !active);
      el.querySelector(".card-weight")!.textContent = `${weight}%`;
      const slider = el.querySelector<HTMLInputElement>("[data-slider]")!;
      if (Number(slider.value) !== weight) slider.value = String(weight);
      for (const seg of el.querySelectorAll<HTMLElement>("[data-phase]")) {
        seg.classList.toggle("is-on", seg.dataset["phase"] === state.phase);
      }
    }
  }

  /**
   * Where transport is measured to: `label` on the card, and the picker's
   * default marked when that is what is chosen.
   */
  setDestination(label: string, isDefault: boolean): void {
    this.destination = label;
    const name = this.cards.querySelector("[data-dest-name]");
    if (name) name.textContent = label;
    this.picker.querySelector("[data-action='reset-destination']")!
      .classList.toggle("is-on", isDefault);
  }

  /**
   * Open or put away the destination picker. It takes the cards' place, in
   * the same corner or the same sheet, so the map beside or above it stays
   * free for choosing on.
   */
  setPicker(open: boolean): void {
    this.picker.hidden = !open;
    this.root.classList.toggle("picking", open);
    if (open && this.isSheet()) this.root.classList.add("sheet-open");
  }

  /** The area panel's content, or null to close it. */
  setPanel(html: string | null): void {
    if (html === null) {
      this.panel.hidden = true;
      this.panel.innerHTML = "";
      this.root.classList.remove("panel-open");
      return;
    }
    this.panel.innerHTML = html;
    this.panel.hidden = false;
    this.root.classList.add("panel-open");
  }

  /**
   * Fill the ad slot, or empty it.
   *
   * The creative is written on every showing rather than left in place, because
   * a provider that is asked for one is asked per impression; the placeholder
   * is merely cheap enough not to care. `strip-open` is what lifts the sheets
   * and the legend clear of it on a narrow screen. With a blocker running the
   * slot opens all the same, on the banner, and stops calling itself an ad so
   * that the blocker leaves the banner alone.
   */
  setAd(open: boolean, blocked = false): void {
    const body = this.ad.querySelector("[data-strip-body]")!;
    body.innerHTML = open ? (blocked ? blockedHtml() : placeholderHtml()) : "";
    this.ad.setAttribute("aria-label", blocked ? "A note from PostcodeHunt" : "Advertisement");
    this.ad.hidden = !open;
    this.root.classList.toggle("strip-open", open);
  }

  /** The slot's 320x50 box, for a provider to fill after `setAd` opens it. */
  get adBody(): HTMLElement {
    return this.ad.querySelector<HTMLElement>("[data-strip-body]")!;
  }

  /** A line under everything for what could not be loaded. */
  setStatus(text: string): void {
    this.status.textContent = text;
    this.status.hidden = !text;
  }
}
